/**
 * Local design tokens for the Candidate Painel evaluation screen.
 *
 * Rebased 2026-07-18 (design review) to mirror `/DESIGN.md`'s palette values
 * exactly — comp-coral, ink-primary, Sand White, forest-pulse. Key names are
 * unchanged so call sites don't churn; only the underlying values moved. The
 * dialect stays LA-local (the rest of labs uses the `--r-*` token system;
 * this surface renders with inline styles, as the source design did) —
 * deviations from the product palette (the violet "a verificar" motif, chip
 * data-colors) are recorded in `docs/labs/level-assessment/design-standard.md`.
 */

export const C = {
  red: "#f3354b",
  redHover: "#db2f43",
  red10: "rgba(243,53,75,0.10)",
  red5: "rgba(243,53,75,0.05)",
  red20: "rgba(243,53,75,0.20)",
  // radar in-band wedge/marker tint (between red10 and red20)
  red12: "rgba(243,53,75,0.12)",
  ink: "#1f1b17",
  ink70: "rgba(31,27,23,0.70)",
  // ink50's alpha is 0.62, not 0.50 — contrast fix. 0.50 computes to ~4.0:1 on
  // white, below WCAG AA for the 12-13px text it's used on; 0.62 is ~4.9:1.
  ink50: "rgba(31,27,23,0.62)",
  ink20: "rgba(31,27,23,0.20)",
  ink10: "rgba(31,27,23,0.10)",
  ink5: "rgba(31,27,23,0.05)",
  // medium-emphasis border tint (level pills, ghost buttons)
  ink14: "rgba(31,27,23,0.14)",
  // form input border
  ink16: "rgba(31,27,23,0.16)",
  // modal/backdrop dim + heavy shadow tint
  ink28: "rgba(31,27,23,0.28)",
  // level-whisker out-of-range tick/rail
  ink32: "rgba(31,27,23,0.32)",
  white: "#fff",
  paper: "#f3f2f1",
  border: "rgba(31,27,23,0.10)",
  borderSoft: "rgba(31,27,23,0.06)",
  green: "#1d9e75",
  greenBg: "rgba(38,142,108,0.13)",
  greenFg: "#1B6148",
  amberBg: "rgba(255,152,17,0.18)",
  amberFg: "#9A5A06",
  // alert-red (DESIGN.md) — the "failed"/error state word (Word-Carries-State)
  alertRed: "#c12a3d",
  violet: "#910048",
  violetBg: "rgba(145,0,72,0.12)",
} as const;

/** Font-family helpers. Fall back to system fonts when the brand faces aren't
 *  loaded in the host page. */
export const UI = { fontFamily: "Inter, system-ui, -apple-system, sans-serif" } as const;
/** Alias — DM Sans was retired per the Inter-Only Rule; display hierarchy
 *  comes from weight 600 + tracking, not a second family. */
export const DM = { fontFamily: UI.fontFamily } as const;
/** Alias — mono retired per the Inter-Only Rule; numeric/id chrome uses Inter
 *  tabular-nums. */
export const MONO = {
  fontFamily: UI.fontFamily,
  fontVariantNumeric: "tabular-nums",
} as const;

/** Soft per-cell character target for the matrix editor's progress bar. This is
 *  a writing-quality nudge, NOT a validation threshold — cells never fail for
 *  being under it. Mirrors the design's `TARGET`. */
export const CELL_TARGET = 1000;

/** Fill state of a single rubric cell from its text length. Drives the status
 *  dot in the matrix level accordion. Mirrors the design's `status()`:
 *  empty (0) → `empty`; thin (< 300) → `partial`; otherwise → `good`. */
export function cellStatus(len: number): "empty" | "partial" | "good" {
  if (len === 0) return "empty";
  if (len < 300) return "partial";
  return "good";
}

/** `L4` / `—` for a (possibly null) level. When a model-wide `names` map is
 *  supplied and the level has a custom name, renders `L4 · Senior`. Backward
 *  compatible: omit `names` (or no name for the level) → plain `L{n}`. */
export function levelLabel(
  level: number | null | undefined,
  names?: Record<string, string> | null,
): string {
  if (level == null) return "—";
  const name = names?.[String(level)]?.trim();
  return name ? `L${level} · ${name}` : `L${level}`;
}

/** The model's level rail as an ascending number array, e.g. [0,1,…,7]. */
export function levelRange(levels: { min: number; max: number }): number[] {
  const out: number[] = [];
  for (let n = levels.min; n <= levels.max; n++) out.push(n);
  return out;
}

/** Confidence (0–1) → tone bucket for chips/meters. Threshold mirrors the
 *  design's 60% "inferable" cutoff. */
export function confidenceTone(p: number): { fg: string; bg: string } {
  if (p < 0.6) return { fg: C.ink50, bg: C.ink5 };
  if (p < 0.75) return { fg: C.amberFg, bg: C.amberBg };
  return { fg: C.greenFg, bg: C.greenBg };
}
